// fastTrackUpsell.js
// Priority Physician Review — the post-purchase charge.
//
// The add-on used to be a pre-ticked box inside checkout. It is now offered
// AFTER the order is captured, from a yes/no modal, and is therefore a second,
// independent charge: POST /api/upsell-checkout with `useExistingCard`, which
// bills the card the order was just placed with. Nothing here can affect the
// order that already succeeded — a decline, a timeout or a dead endpoint costs
// the patient nothing and leaves their prescription untouched.
//
// The price comes from the resolved catalog record (fastTrackAddon.js), never
// from a literal here, so flipping the product inactive removes the offer with
// no deploy.
//
// Two ways in, because the payment paths end in different places:
//
//   • Card / wallet order — omit `cardToken`. `useExistingCard` bills the card
//     the order was just placed with; nothing is re-collected.
//   • Klarna order — pass `cardToken`. Klarna authorizes one fixed amount up
//     front and leaves nothing on file, so there is no card for
//     `useExistingCard` to find. The modal collects one and hands the
//     PaymentMethod id over here.
//
// Same endpoint and the same body shape the existing upsell page already posts.

// The DEFAULT axios instance on purpose, never axios.create(). main.js registers
// the request interceptor that attaches `Authorization: Bearer <token>` on this
// singleton, and instances made with axios.create() do not inherit it.
// /api/upsell-checkout is an authenticated route, so a private instance gets
// E_UNAUTHORIZED_ACCESS every time. This is the same object the checkouts reach
// through this.$axios.
import axios from 'axios';

import { getProducts, fetchProductsFromServer } from './productData';
import { resolveFastTrackAddon } from './fastTrackAddon';

const API_BASE = process.env.VUE_APP_API_ENDPOINT;
// Same budget the main checkout call uses.
const REQUEST_TIMEOUT = 30000;

// AdonisJS rejects with { errors: [{ message }] }, while the app's own handlers
// answer with { message }. Read both, or a real failure reason gets replaced by
// the generic fallback and the next person debugs blind.
function readErrorMessage(data) {
    if (!data) return '';
    if (Array.isArray(data.errors) && data.errors.length > 0 && data.errors[0].message) {
        return data.errors[0].message;
    }
    return data.message || '';
}

// One catalog revalidation per browser session, shared by every checkout the SPA
// visits. See loadFastTrackAddon for why it exists.
let catalogRevalidation = null;

/**
 * Resolve the add-on from the shared product cache. null means "do not offer it"
 * and is a normal outcome — a checkout that gets null goes straight to its
 * receipt.
 *
 * The cache is a one-hour localStorage copy shared by every page, so a browser
 * can easily hold a product list from before the add-on was added or switched
 * back on. `is_active` on the live record is meant to be the only switch, with
 * no deploy needed — silently honouring an hour-old copy defeats that, and shows
 * up as the offer appearing on one checkout and not the next depending on when
 * that browser last filled its cache. So a cache MISS is confirmed against the
 * server before concluding there is nothing to offer; only a real "not in the
 * live catalog" turns the offer off.
 *
 * Never throws: this runs on top of an order that already succeeded, so a dead
 * catalog costs the patient the offer, never their confirmation.
 */
export async function loadFastTrackAddon() {
    try {
        const cached = getProducts();
        if (Array.isArray(cached) && cached.length > 0) {
            const resolved = resolveFastTrackAddon(cached);
            if (resolved) return resolved;
        }

        if (!catalogRevalidation) {
            // Cleared on failure so a transient network error can't silence the
            // offer for the rest of the session.
            catalogRevalidation = fetchProductsFromServer(true).catch((error) => {
                catalogRevalidation = null;
                throw error;
            });
        }
        return resolveFastTrackAddon(await catalogRevalidation);
    } catch (error) {
        console.error('Priority Physician Review add-on unavailable:', error);
        return null;
    }
}

// Klarna redirects off-site and comes back to KlarnaReturn.vue as a fresh page
// load, so the checkout's in-memory context (who to charge, what address to bill)
// is gone by the time the offer can be made. Each checkout stashes it here right
// before the redirect; the return page reads it once and throws it away.
//
// Keyed to the payment intent so a stale stash from an abandoned attempt can
// never attach itself to a different order.
const KLARNA_CONTEXT_KEY = 'pending_fast_track_context';

export function stashFastTrackContext(paymentIntentId, context) {
    try {
        sessionStorage.setItem(
            KLARNA_CONTEXT_KEY,
            JSON.stringify({ payment_intent_id: paymentIntentId, context })
        );
    } catch (e) {
        // sessionStorage unavailable — the Klarna order still completes, it just
        // makes no offer on the way back.
    }
}

/** Single use: reads and clears. null means make no offer. */
export function readFastTrackContext(paymentIntentId) {
    let stash = null;
    try {
        stash = JSON.parse(sessionStorage.getItem(KLARNA_CONTEXT_KEY) || 'null');
        sessionStorage.removeItem(KLARNA_CONTEXT_KEY);
    } catch (e) {
        return null;
    }
    if (!stash || stash.payment_intent_id !== paymentIntentId) return null;
    return stash.context || null;
}

function normalizeAddress(address) {
    const a = address || {};
    return {
        addressLine1: a.addressLine1 || '',
        addressLine2: a.addressLine2 || '',
        city: a.city || '',
        state: a.state || '',
        zip: a.zip || '',
    };
}

/**
 * Charge the add-on.
 *
 * Never throws: the caller is rendering a modal on top of a completed order and
 * has to show a message, not a stack trace. Every failure comes back as
 * { ok: false, message }.
 *
 * args:
 *   addon           — { product, plan } from resolveFastTrackAddon. Required.
 *   userId          — who to charge. Required.
 *   paymentTerms    — billing duration of the order this rides on. The fee
 *                     itself is one-time; this only tags the charge.
 *   cardToken       — Klarna path only: a Stripe PaymentMethod id. Absent means
 *                     "bill the card already on file".
 *   cardBrand/cardLast4 — display only, sent alongside cardToken.
 *   billingAddress / shippingAddress / firstName / lastName / email
 *   trackingSessionId, ipAddress
 */
export async function chargeFastTrackUpsell(args) {
    const addon = args && args.addon;
    if (!addon || !addon.product || !addon.plan) {
        return { ok: false, message: 'Priority review is unavailable right now.' };
    }
    if (!args.userId) {
        return { ok: false, message: 'We could not find your account. Please try again.' };
    }

    const fee = Number(Number(addon.plan.price).toFixed(2));
    if (!Number.isFinite(fee) || fee <= 0) {
        return { ok: false, message: 'Priority review is unavailable right now.' };
    }

    // `terms` is read off the catalog plan rather than assumed: the live record
    // says 0 (one-time), and if that ever changes the charge should follow the
    // catalog instead of a number frozen into this file.
    const terms = Number(addon.plan.terms || 0) || 0;
    const cardToken = args.cardToken || '';

    const body = {
        id: args.userId,
        user_id: args.userId,
        payment_terms: args.paymentTerms || 1,

        // `useExistingCard` tells the backend to bill the card the order was just
        // placed with, and is the only mode a card or wallet order ever uses. A
        // Klarna order has no such card, so it sends a token instead and this
        // flips to false — leaving it true there would ask the backend to charge
        // nothing.
        useExistingCard: !cardToken,
        is_upsell: true,
        paymentMethod: 'card',
        cardToken,
        cardBin: '',
        cardBrand: args.cardBrand || '',
        cardLast4: args.cardLast4 || '',
        cardDetails: [],

        amount: Math.round(fee * 100),
        discount: 0,
        couponCode: '',
        couponId: null,
        // Empty on purpose, exactly as the existing upsell page sends it. On the
        // main checkout `planId` is what the server charges off; here the cart
        // line is, and naming a plan as well is how you get billed twice for one
        // add-on.
        planId: '',
        medication: '',
        drug_mode: '',
        offer: '',

        cartItems: [
            {
                id: addon.product.id,
                product_id: addon.product.id,
                plan_id: addon.plan.id || '',
                name: addon.product.name,
                price: fee,
                retail_price: fee,
                terms,
                quantity: 1,
                // Read off the catalog record rather than asserted, and consistent
                // with the totals below: this order is one line, so that line is
                // the whole of mainProductTotal and addonTotal is 0.
                is_addon: addon.product.is_addon === true,
            },
        ],
        cartTotal: fee,
        cartTotalNoDiscount: fee,
        mainProductTotal: fee,
        addonTotal: 0,
        totalOrderAmount: fee,

        firstName: args.firstName || '',
        lastName: args.lastName || '',
        email: args.email || '',
        billingAddress: normalizeAddress(args.billingAddress),
        shippingAddress: normalizeAddress(args.shippingAddress),

        tracking_session_id: args.trackingSessionId || 'unknown',
        ip_address: args.ipAddress || '',
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
    };

    const headers = {};
    // One UUID per charge attempt — anchors the backend's Stripe idempotency key
    // so a retry of this same attempt cannot create a second charge.
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        headers['X-Correlation-Id'] = crypto.randomUUID();
    }

    try {
        const response = await axios.post(`${API_BASE}/api/upsell-checkout`, body, {
            headers,
            timeout: REQUEST_TIMEOUT,
        });
        const data = response.data || {};

        if (data.status === 'success') {
            const transactionId = data.transaction_id || data.transactionId || data.pendingAuthId;
            return {
                ok: true,
                transactionId,
                orderId: data.order_id || data.orderId || transactionId,
            };
        }

        return {
            ok: false,
            message: readErrorMessage(data) || 'We could not add priority review. Your order is still confirmed.',
        };
    } catch (error) {
        console.error('[fast-track] priority review upsell failed:', error);
        return {
            ok: false,
            message:
                readErrorMessage(error.response?.data) ||
                'We could not add priority review. Your order is still confirmed.',
        };
    }
}

/**
 * Params for the add-on's own `purchase` event.
 *
 * It gets its own event rather than being folded into the order's: that one
 * already fired for the medication alone, and adding this revenue to it would
 * mean a second event wearing the same ids. For the same reason `transaction_id`
 * and `order_id` are carried ONLY when the server returned them — reusing the
 * order's would hand the ad platforms two purchases on one transaction, which is
 * how one of them gets dropped as a duplicate. Inventing an id would be worse.
 *
 * Each checkout numbers its own steps and owns its own `form`, so the caller
 * passes both. Without `form` the add-on's revenue lands outside every funnel
 * and can't be reported against the order it rode in on.
 */
export function buildFastTrackPurchaseParams(addon, result, funnelStep, form) {
    const params = {
        funnel_step: funnelStep,
        form,
        event_value: Number(Number(addon.plan.price).toFixed(2)),
        currency: 'USD',
        event_id: crypto.randomUUID(),
        product_id: addon.product.id,
        product_name: addon.product.name,
        quantity: 1,
        label: 'fast_track_upsell',
    };
    if (addon.plan.id) params.plan_id = addon.plan.id;
    if (result && result.transactionId) params.transaction_id = result.transactionId;
    if (result && result.orderId) params.order_id = result.orderId;
    return params;
}
